// @ts-expect-error - data-catalog-js-api is not typed (yet)
import { JsonFileDataSource, ReportDataProcessor } from 'data-catalog-js-api';
import pako from 'pako'; // These two libraries would be nicer to load dynamically but that doesn't quite work with offline mode, so...
import {
    createStore as createIdbStore,
    get as idbGet,
    set as idbSet,
    del as idbDelete,
    keys as idbKeys
} from 'idb-keyval';

type NamedItem = {
    id: string;
    name: string | undefined;
};

type DownloadResult = {
    url: string;
    timestamp: string;
    label: string;
};

export const RB_IDB_DATABASE_ID: string = 'esriuk-rb-local-data';
export const RB_IDB_STORE_ID: string = 'esriuk-rb-local-reports';

export const downloadReportToBrowser = async (
    reportItem: any,
    activeFeature: any,
    token: string,
    portalUrl: string,
    specificTimestamp?: string | null | undefined
): Promise<DownloadResult> => {
    //import('idb-keyval').then(({ Store: IdbStore, set: idbSet, get: idbGet }) =>
    //{
    //    import('pako').then(pako =>
    //    {
    const customIdbStore = createIdbStore(RB_IDB_DATABASE_ID, RB_IDB_STORE_ID),
        { info: itemInfo, report, data: itemData } = reportItem,
        processor = new ReportDataProcessor(report, token, portalUrl);
    if (activeFeature === null) throw new Error('Cannot export with null feature');
    await processor.init();
    const af: NamedItem = Array.isArray(activeFeature) ? activeFeature[0] : activeFeature,
        featureDataTables: any = await processor.getData(af),
        isTimeStamped = specificTimestamp !== undefined && specificTimestamp !== null && specificTimestamp !== '',
        reportKey = isTimeStamped
            ? `rb4arcgisDesign$R${itemInfo.id}$T${specificTimestamp}`
            : `rb4arcgisDesign$R${itemInfo.id}`,
        featuresKey = isTimeStamped ? `rbFeatures$R${itemInfo.id}$T${specificTimestamp}` : `rbFeatures$R${itemInfo.id}`,
        dataKey = isTimeStamped
            ? `rbData$R${itemInfo.id}$F${af.id}$T${specificTimestamp}`
            : `rbData$R${itemInfo.id}$F${af.id}`,
        storageObject: any = {
            info: {
                id: itemInfo.id,
                title: itemInfo.title,
                description: itemInfo.description,
                tags: [...itemInfo.tags]
            },
            data: {
                values: {
                    ...itemData.values
                }
            },
            report: {
                ...report
            }
        };
    if (isTimeStamped) storageObject.downloaded = specificTimestamp;
    // Minimal design summary - to be re-used
    let jsonString = JSON.stringify(storageObject);
    await idbSet(reportKey, pako.gzip(jsonString), customIdbStore);
    // Data summary for the active feature...
    jsonString = JSON.stringify({
        feature: activeFeature,
        tables: featureDataTables
    });
    await idbSet(dataKey, pako.gzip(jsonString), customIdbStore);
    const flistBlob: any = await idbGet(featuresKey, customIdbStore);
    let listPromise = Promise.resolve();
    const localList =
        flistBlob !== undefined
            ? JSON.parse(pako.inflate(flistBlob, { to: 'string' }))
            : {
                  id: itemInfo.id,
                  title: itemInfo.title,
                  description: itemInfo.description,
                  tags: [...itemInfo.tags], // Basic summary so that any list generator can make something useful...
                  features: []
              };
    if (localList.features.find((f: any) => f.id === af.id) === undefined) {
        localList.features.push({
            id: af.id,
            name: af.name
        });
        listPromise = idbSet(featuresKey, pako.gzip(JSON.stringify(localList)), customIdbStore);
    }
    await listPromise;
    const now: string = new Date()
        .toISOString()
        .substring(0, 'yyyy-MM-ddTHH:mm:ss'.length)
        .replace(/[^0-9a-zA-Z]/g, '');
    return {
        url: isTimeStamped
            ? `/view-local/${itemInfo.id}/${af.id}?t=${specificTimestamp}`
            : `/view-local/${itemInfo.id}/${af.id}`,
        timestamp: isTimeStamped ? specificTimestamp : now,
        label: itemInfo.title
    };
};

type DataStorageResult = {
    key: string;
    timestamp?: string;
    stored?: number;
};

export const downloadReportDesignToBrowser = async (
    reportId: string,
    report: any,
    specificTimestamp?: string | null | undefined
): Promise<DataStorageResult> => {
    const customIdbStore = createIdbStore(RB_IDB_DATABASE_ID, RB_IDB_STORE_ID);
    const isTimeStamped = specificTimestamp !== undefined && specificTimestamp !== null && specificTimestamp !== '',
        reportKey = isTimeStamped
            ? `rb4arcgisDesignOnly$R${reportId}$T${specificTimestamp}`
            : `rb4arcgisDesignOnly$R${reportId}`,
        storageObject: any = {
            report: {
                ...report
            }
        },
        now = new Date();
    if (isTimeStamped) storageObject.downloaded = specificTimestamp;
    storageObject.stored = now.getTime();
    // Minimal design summary - to be re-used
    let jsonString = JSON.stringify(storageObject);
    await idbSet(reportKey, pako.gzip(jsonString), customIdbStore);
    const nowText: string = now
        .toISOString()
        .substring(0, 'yyyy-MM-ddTHH:mm:ss'.length)
        .replace(/[^0-9a-zA-Z]/g, '');
    return {
        key: reportKey,
        timestamp: isTimeStamped ? specificTimestamp : nowText,
        stored: now.getTime()
    };
};

export const deleteReportFromBrowser = async (
    reportItem: any,
    activeFeature: any,
    specificTimestamp?: string | null | undefined
): Promise<boolean> => {
    const customIdbStore = createIdbStore(RB_IDB_DATABASE_ID, RB_IDB_STORE_ID),
        { info: itemInfo } = reportItem,
        reportKey =
            specificTimestamp !== undefined && specificTimestamp !== null && specificTimestamp !== ''
                ? `rb4arcgisDesign$R${itemInfo.id}$T${specificTimestamp}`
                : `rb4arcgisDesign$R${itemInfo.id}`,
        dataStemKey = `rbData$R${itemInfo.id}$F`,
        featuresKey =
            specificTimestamp !== undefined && specificTimestamp !== null && specificTimestamp !== ''
                ? `rbFeatures$R${itemInfo.id}$T${specificTimestamp}`
                : `rbFeatures$R${itemInfo.id}`,
        af: NamedItem | null = Array.isArray(activeFeature) ? activeFeature[0] : activeFeature;
    if (af === null) throw new Error('Cannot find local data without feature ID');
    const dataKey =
        specificTimestamp !== undefined && specificTimestamp !== null && specificTimestamp !== ''
            ? `rbData$R${itemInfo.id}$F${af.id}$T${specificTimestamp}`
            : `rbData$R${itemInfo.id}$F${af.id}`;
    await idbDelete(dataKey, customIdbStore);
    const localKeySet: any[] = await idbKeys(customIdbStore),
        otherFeatures = localKeySet.filter((k) => k.toString().indexOf(dataStemKey) === 0);
    if (otherFeatures.length < 1) {
        await idbDelete(reportKey, customIdbStore);
        await idbDelete(featuresKey, customIdbStore);
        return true;
    } else {
        const flistBlob: any = await idbGet(featuresKey, customIdbStore);
        let listPromise = Promise.resolve(),
            listFeatureIndex = -1;
        const localList =
            flistBlob !== undefined
                ? JSON.parse(pako.inflate(flistBlob, { to: 'string' }))
                : {
                      id: itemInfo.id,
                      title: itemInfo.title,
                      description: itemInfo.description,
                      tags: [...itemInfo.tags], // Basic summary so that any list generator can make something useful...
                      features: []
                  };
        if ((listFeatureIndex = localList.features.findIndex((f: any) => f.id === af.id)) >= 0) {
            localList.features.splice(listFeatureIndex, 1);
            if (localList.features.length < 1) listPromise = idbDelete(featuresKey, customIdbStore);
            else listPromise = idbSet(featuresKey, pako.gzip(JSON.stringify(localList)), customIdbStore);
        }
        await listPromise;
        return true;
    }
};

export const deleteReportDesignFromBrowser = async (
    reportId: string,
    specificTimestamp?: string | null | undefined
): Promise<boolean> => {
    const customIdbStore = createIdbStore(RB_IDB_DATABASE_ID, RB_IDB_STORE_ID),
        reportKey =
            specificTimestamp !== undefined && specificTimestamp !== null && specificTimestamp !== ''
                ? `rb4arcgisDesignOnly$R${reportId}$T${specificTimestamp}`
                : `rb4arcgisDesignOnly$R${reportId}`;
    await idbDelete(reportKey, customIdbStore);
    return true;
};

export const fetchReportFromBrowser = async (
    reportId: string,
    specificTimestamp?: string | null | undefined
): Promise<any> => {
    const customIdbStore = createIdbStore(RB_IDB_DATABASE_ID, RB_IDB_STORE_ID),
        designKey =
            specificTimestamp !== undefined && specificTimestamp !== null && specificTimestamp !== ''
                ? `rb4arcgisDesign$R${reportId}$T${specificTimestamp}`
                : `rb4arcgisDesign$R${reportId}`,
        reportJson = await idbGet(designKey, customIdbStore);
    return JSON.parse(pako.inflate(reportJson, { to: 'string' }));
};

export const fetchReportDesignFromBrowser = async (
    reportId: string,
    specificTimestamp?: string | null | undefined
): Promise<any> => {
    const customIdbStore = createIdbStore(RB_IDB_DATABASE_ID, RB_IDB_STORE_ID),
        designKey =
            specificTimestamp !== undefined && specificTimestamp !== null && specificTimestamp !== ''
                ? `rb4arcgisDesignOnly$R${reportId}$T${specificTimestamp}`
                : `rb4arcgisDesignOnly$R${reportId}`,
        reportJson = await idbGet(designKey, customIdbStore);
    return JSON.parse(pako.inflate(reportJson, { to: 'string' }));
};

export const isReportDesignAvailableInBrowser = async (
    reportId: string,
    specificTimestamp?: string | null | undefined
): Promise<any> => {
    const customIdbStore = createIdbStore(RB_IDB_DATABASE_ID, RB_IDB_STORE_ID),
        designKey =
            specificTimestamp !== undefined && specificTimestamp !== null && specificTimestamp !== ''
                ? `rb4arcgisDesignOnly$R${reportId}$T${specificTimestamp}`
                : `rb4arcgisDesignOnly$R${reportId}`,
        reportKeys = await idbKeys(customIdbStore);
    return reportKeys.indexOf(designKey) >= 0;
};

export const fetchReportDataFromBrowser = async (
    reportId: string,
    featureIds: string,
    token?: string | null | undefined,
    specificTimestamp?: string | null | undefined
): Promise<JsonFileDataSource> => {
    const customIdbStore = createIdbStore(RB_IDB_DATABASE_ID, RB_IDB_STORE_ID),
        coreFeatureUrl =
            specificTimestamp !== undefined && specificTimestamp !== null && specificTimestamp !== ''
                ? `rbData$R${reportId}$F${featureIds}$T${specificTimestamp}`
                : `rbData$R${reportId}$F${featureIds}`,
        dataBlob = await idbGet(coreFeatureUrl, customIdbStore);
    return new JsonFileDataSource(pako.inflate(dataBlob, { to: 'string' }), token, 0);
};

export const hasLocalData = async (key: string): Promise<boolean> => {
    const customIdbStore = createIdbStore(RB_IDB_DATABASE_ID, RB_IDB_STORE_ID);
    const dataKey = `rb4arcgisData$D${key}`;
    const validKeys = await idbKeys(customIdbStore);
    return validKeys.findIndex((k) => k.toString() === dataKey) >= 0;
};

export const clearLocalData = async (key: string): Promise<boolean> => {
    const customIdbStore = createIdbStore(RB_IDB_DATABASE_ID, RB_IDB_STORE_ID);
    const dataKey = `rb4arcgisData$D${key}`;
    const validKeys = await idbKeys(customIdbStore);
    if (validKeys.findIndex((k) => k.toString() === dataKey) >= 0) {
        await idbDelete(dataKey, customIdbStore);
        return true;
    }
    return false;
};

export const storeLocalData = async (key: string, data: any): Promise<DataStorageResult> => {
    const customIdbStore = createIdbStore(RB_IDB_DATABASE_ID, RB_IDB_STORE_ID);
    const dataKey = `rb4arcgisData$D${key}`,
        now = new Date();
    // Minimal design summary - to be re-used
    let jsonString = JSON.stringify(data);
    await idbSet(dataKey, pako.gzip(jsonString), customIdbStore);
    const nowText: string = now
        .toISOString()
        .substring(0, 'yyyy-MM-ddTHH:mm:ss'.length)
        .replace(/[^0-9a-zA-Z]/g, '');
    return {
        key: dataKey,
        stored: now.getTime()
    };
};

export const fetchLocalData = async (key: string): Promise<any> => {
    const customIdbStore = createIdbStore(RB_IDB_DATABASE_ID, RB_IDB_STORE_ID);
    const dataKey = `rb4arcgisData$D${key}`;
    try {
        const dataJsonCompressed = await idbGet(dataKey, customIdbStore);
        return JSON.parse(pako.inflate(dataJsonCompressed, { to: 'string' }));
    } catch (jsonFetchErr) {
        console.debug(jsonFetchErr);
        return undefined;
    }
};

// export const generateUserKey = async (user: any, format?: 'hex' | 'base64'): Promise<string> => {
//     if (user !== undefined && user !== null && user.username) {
//         const msgUint8 = new TextEncoder().encode(user.username); // encode as (utf-8) Uint8Array
//         const hashBuffer = await window.crypto.subtle.digest('SHA-256', msgUint8); // hash the message
//         const uint = new Uint8Array(hashBuffer);
//         if (typeof (uint as any).toBase64 !== 'undefined' && format !== 'hex') {
//             const b64 = (uint as any).toBase64();
//             return b64;
//         } else {
//             const hashArray = Array.from(new Uint8Array(hashBuffer)); // convert buffer to byte array
//             const hashHex = hashArray.map((b) => b.toString(16).padStart(2, '0')).join(''); // convert bytes to hex string
//             return hashHex;
//         }
//     }
//     return '';
// };

export const generateUserKey = (user: any, format?: 'hex' | 'base64'): string => {
    if (user !== undefined && user !== null && user.username) {
        const msgUint8 = new TextEncoder().encode(user.username); // encode as (utf-8) Uint8Array
        if (typeof (msgUint8 as any).toBase64 !== 'undefined' && format !== 'hex') {
            const b64 = (msgUint8 as any).toBase64();
            return b64;
        } else {
            const hashArray = Array.from(new Uint8Array(msgUint8)); // convert buffer to byte array
            const hashHex = hashArray.map((b) => b.toString(16).padStart(2, '0')).join(''); // convert bytes to hex string
            return hashHex;
        }
    }
    return '';
};

export const createGzippedString = (inputText: string, format?: 'hex' | 'base64'): string => {
    const zipped = pako.gzip(inputText);
    if (typeof (zipped as any).toBase64 !== 'undefined' && format !== 'hex') {
        const b64 = (zipped as any).toBase64();
        return b64;
    } else {
        const hashArray = Array.from(new Uint8Array(zipped)); // convert buffer to byte array
        const hashHex = hashArray.map((b) => b.toString(16).padStart(2, '0')).join(''); // convert bytes to hex string
        return hashHex;
    }
};
